import { FormControl, InputBase, InputLabel, ListItemText, MenuItem, Select } from "@mui/material";
import _ from "lodash";
import CheckboxButton from "new-components/buttons/checkbox-button";
import { useCallback, useEffect, useRef, useState } from "react";
import { HelperText } from "./helper-text";

const SEARCH_OPTION_ID = "__select_checkbox_search__";

interface Option {
    id: any;
    name: string;
    disabled?: boolean;
}

interface SelectCheckBoxInputProps {
    onChange: (selected: any[]) => void;
    options: Option[];
    loading?: boolean;
    fullWidth?: boolean;
    selected: any;
    helperText?: string;
    disabled?: boolean;
    label?: string;
    required?: boolean;
    width?: string | number;
    height?: string | number;
    tranformOption?: boolean;
    disabledAll?: boolean;
    register?: any;
    sx?: any;
    /** เมื่อเปิดใช้ จะแสดงช่องค้นหาด้านบนและกรอง options ตามข้อความที่พิมพ์ */
    searchable?: boolean;
    searchPlaceholder?: string;
    /** เมื่อกำหนด จะใช้ server-side search แทน client-side filter (debounce 300ms) */
    onSearch?: (search: string) => void;
    /** เรียกเมื่อเปิด dropdown */
    onOpen?: () => void;
    /** เรียกเมื่อ scroll ถึงล่างสุด เพื่อโหลดข้อมูลเพิ่ม */
    onLoadMore?: () => void;
    /** ยังมีข้อมูลให้โหลดเพิ่มอีกหรือไม่ */
    hasMore?: boolean;
}

export default function SelectCheckBoxInput(props: SelectCheckBoxInputProps) {
    const selectRef = useRef<HTMLDivElement>(null);
    const [menuWidth, setMenuWidth] = useState<string | number>("auto");
    const [searchQuery, setSearchQuery] = useState("");

    const onSearchRef = useRef(props.onSearch);
    onSearchRef.current = props.onSearch;

    const debouncedOnSearch = useCallback(
        _.debounce((text: string) => {
            onSearchRef.current?.(text)
        }, 300),
        []
    );

    const handleSearchChange = (text: string) => {
        setSearchQuery(text)
        if (props.onSearch) {
            debouncedOnSearch(text)
        }
    }

    useEffect(() => {
        if (selectRef.current) {
            setMenuWidth(selectRef.current.clientWidth);
        }
    }, [props.width, props.fullWidth]);

    const allOption: Option = { id: null, name: "ทั้งหมด" };

    // ถ้ามี onSearch ใช้ server-side search (ไม่ต้อง filter client-side)
    // ถ้าไม่มี onSearch ใช้ client-side filter เหมือนเดิม
    const filteredOptions =
        props.searchable && searchQuery.trim() && !props.onSearch
            ? props.options.filter((opt) =>
                  String(opt.name || "")
                      .toLowerCase()
                      .includes(searchQuery.trim().toLowerCase())
              )
            : props.options;

    // 1) ไอดีของ options ที่ไม่ได้ disabled (ใช้จาก filteredOptions เมื่อมี search)
    const selectableOptionIds = filteredOptions
        .filter((opt) => opt.id !== null && !opt.disabled)
        .map((opt) => opt.id);

    // 2) ไอดีของ options ที่ disabled แต่ props.selected มีอยู่
    const disabledSelectedIds = props.options
        .filter((opt) => opt.disabled && props.selected.includes(opt.id))
        .map((opt) => opt.id);

    // สถานะ "เลือกทั้งหมด" (เฉพาะกลุ่มที่แสดง)
    const allSelected =
        selectableOptionIds.length > 0 &&
        selectableOptionIds.every((id) => props.selected.includes(id));

    // สถานะ "เลือกบางส่วน"
    const partialSelected =
        selectableOptionIds.some((id) => props.selected.includes(id)) &&
        !allSelected;

    const handleChange = (event: any) => {
        let value = event.target.value as any[];
        if (Array.isArray(value)) {
            value = value.filter((v: any) => v !== SEARCH_OPTION_ID);
        }

        if (value.includes(allOption.id)) {
            if (allSelected) {
                props.onChange(disabledSelectedIds);
            } else {
                props.onChange([...selectableOptionIds, ...disabledSelectedIds]);
            }
        } else {
            props.onChange(value);
        }
    };

    const handleClose = () => {
        setSearchQuery("");
        if (props.onSearch) props.onSearch("");
    };

    const handleOpen = () => {
        props.onOpen?.();
    };

    const handleScroll = (event: any) => {
        if (!props.onLoadMore || !props.hasMore || props.loading) return;
        const target = event.currentTarget || event.target;
        const { scrollTop, scrollHeight, clientHeight } = target;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            props.onLoadMore();
        }
    };

    return (
        <FormControl
            ref={selectRef}
            sx={{ width: props.width || "100%" }}
            size="small"
            disabled={props.disabled}
        >
            <InputLabel
                id="demo-select-small-label"
                sx={{
                    ...props.sx,
                    // ยึดจากด้านบนของ input ไม่ใช่ 50% ของ FormControl
                    top: 0,
                    transform: 'translate(14px, 9px)',        // ตำแหน่งปกติ (size="small")
                    transformOrigin: 'left top',
                    display: 'flex',
                    alignItems: 'center',
                    color: props.helperText ? '#B00020' : '#808080',
                    '&.Mui-focused': {
                        color: props.helperText ? '#B00020' : '#1CB99A',
                        '& span': {
                            color: '#B00020', // สีแดงตอน focus
                        },
                    },
                    '&.MuiInputLabel-shrink': {
                        top: 0,
                        transform: 'translate(14px, -6px) scale(0.75)', // ขณะหด
                        transformOrigin: 'left top',
                    },
                }}
            >
                {props.label} {props.required && <span>*</span>}
            </InputLabel>

            <Select
                multiple
                error={props.helperText !== "" && props.helperText !== undefined}
                fullWidth={props.fullWidth}
                value={(props.selected || []).filter((v: any) => v !== SEARCH_OPTION_ID)}
                onChange={handleChange}
                onOpen={handleOpen}
                onClose={handleClose}
                inputProps={{ value: props.selected }}
                MenuProps={{
                    PaperProps: {
                        sx: { width: menuWidth, maxHeight: '400px', overflow: 'auto' },
                        onScroll: handleScroll,
                    },
                    ...(props.tranformOption
                        ? {
                            anchorOrigin: { vertical: "top", horizontal: "left" },
                            transformOrigin: { vertical: "bottom", horizontal: "left" },
                        }
                        : {
                            disableScrollLock: true,
                            disableAutoFocusItem: true,
                        }),
                }}
                renderValue={(selectedIds) => {
                    const selectedOptionIds = (selectedIds || []).filter(
                        (id: any) => id !== null && id !== "ALL" && id !== SEARCH_OPTION_ID
                    );
                    if (
                        props.options.length > 0 &&
                        selectedOptionIds.length === props.options.length
                    ) {
                        return "ทั้งหมด";
                    }
                    return props.options
                        .filter((option) => selectedOptionIds.includes(option.id))
                        .map((option) => option.name)
                        .join(", ");
                }}
                sx={{
                    height: props.height || 40,
                    backgroundColor: props.disabled ? "#F0F0F0" : "#FFFFFF",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1CB99A",
                        borderWidth: "1px",
                    },
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#B00020",
                    },
                    "&.Mui-disabled": { borderColor: "#C5C5C5" },
                }}
            >
                {props.searchable && (
                    <MenuItem
                        value={SEARCH_OPTION_ID}
                        sx={{
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                            py: 0.5,
                            "&:hover": { backgroundColor: "#fff" },
                            "&.Mui-focusVisible": { backgroundColor: "#fff" },
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <InputBase
                            fullWidth
                            placeholder={props.searchPlaceholder ?? "ค้นหา..."}
                            value={searchQuery}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            sx={{
                                py: 1,
                                px: 1.5,
                                fontSize: "14px",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                "&.Mui-focused": { borderColor: "#1CB99A" },
                            }}
                            inputProps={{ "aria-label": "ค้นหา" }}
                        />
                    </MenuItem>
                )}

                {filteredOptions.length === 0 && (
                    <MenuItem
                        disabled
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            color: "#000",
                            opacity: 1,
                            "&.Mui-disabled": { color: "#000", opacity: 1 },
                        }}
                    >
                        {props.searchable && searchQuery.trim() ? "ไม่พบรายการที่ตรงกับคำค้น" : "ไม่พบข้อมูล"}
                    </MenuItem>
                )}

                {filteredOptions.length > 0 && (
                    <MenuItem key={allOption.id} value={allOption.id} disabled={props.disabledAll}>
                        <CheckboxButton
                            indeterminate={partialSelected}
                            checked={allSelected}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    props.onChange([...selectableOptionIds, ...disabledSelectedIds]);
                                } else {
                                    props.onChange(disabledSelectedIds);
                                }
                            }}
                        />
                        <ListItemText
                            primary={allOption.name}
                            sx={{
                                whiteSpace: "normal",
                                overflowWrap: "break-word",
                            }}
                        />
                    </MenuItem>
                )}

                {props.loading && filteredOptions.length === 0 ? (
                    <MenuItem disabled>กำลังโหลด...</MenuItem>
                ) : (
                    _.map(filteredOptions, (item, index) => (
                        <MenuItem
                            key={index}
                            disabled={item.disabled}
                            value={item.id}
                            sx={{
                                whiteSpace: "normal",
                                overflowWrap: "break-word",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                "&.Mui-focusVisible": { backgroundColor: "#FFFFFF" },
                                "&:hover": { backgroundColor: "#EBF2F0" },
                                "&.Mui-selected.Mui-focusVisible": { backgroundColor: "#EBF2F0" },
                                "&.Mui-selected": { backgroundColor: "transparent" },
                                "&.Mui-selected:hover": { backgroundColor: "#EBF2F0" },
                            }}
                        >
                            <CheckboxButton onChange={() => null} checked={props.selected.includes(item.id)} />
                            <ListItemText
                                primary={item.name}
                                sx={{
                                    whiteSpace: "normal",
                                    overflowWrap: "break-word",
                                }}
                            />
                        </MenuItem>
                    ))
                )}
                {props.loading && filteredOptions.length > 0 && (
                    <MenuItem disabled sx={{ display: 'flex', justifyContent: 'center' }}>กำลังโหลด...</MenuItem>
                )}
            </Select>

            {props.helperText && <HelperText label={props.helperText} />}
        </FormControl>
    );
}
